import { useEffect, useState } from 'react';
import { getReglagesPublics } from '../api/reglagesApi';
import {
  appliquerBlueSky, blueSkyEnregistre, saisonEnregistree, saisonValide,
} from './styleSite';
import { demarrerFluxReglages } from './fluxReglages';

/**
 * Les drapeaux publics du service, lus une fois et partagés.
 *
 * Interrogés une SEULE fois par session et partagés entre tous les appelants.
 * La barre de navigation, le pied de page, l'accueil et la page des tarifs en
 * ont besoin ; un aller-retour par composant ferait quatre appels pour deux
 * booléens qui ne changent pas d'une seconde à l'autre.
 *
 * VALEURS DE DÉPART : celles du produit OUVERT — mode test éteint, essais
 * ouverts. Tant qu'on ne sait pas, on n'affiche rien de différent : un site qui
 * masque ses tarifs une demi-seconde puis les fait réapparaître est pire qu'un
 * site qui les garde. Et un bouton qui dit « Commencer gratuitement » puis se
 * change en « S'inscrire » sous le curseur donne l'impression d'une offre
 * retirée à la dernière seconde.
 *
 * Le bandeau, lui, part à `null` : afficher un avertissement sur une panne
 * de lecture inquiéterait sans rien apprendre.
 */
const PAR_DEFAUT = {
  modeTest: false, essaisOuverts: true, maintenance: false, bandeau: null,

  // LE STYLE, LUI, PART DE CE QUE L'APPAREIL A VU LA DERNIÈRE FOIS — le même
  // que `public/index.html` a déjà posé sur la page. Partir de « éteint »
  // ferait afficher l'ancienne page d'attente un instant avant la nouvelle.
  blueSky: blueSkyEnregistre(),

  // Sa déclinaison (soldes, French Days, Black Friday), même raison.
  styleSaison: saisonEnregistree(),

  // ÉTEINT TANT QU'ON N'A RIEN LU : on n'ouvre pas une connexion qui peut
  // durer des heures sur une valeur qu'on n'a pas encore reçue. La première
  // lecture réussie la remplace aussitôt.
  fluxSse: false,

  // LES JEUX, CYCLE PAR CYCLE. Éteints tant qu'on n'a rien lu : montrer une
  // porte qui n'ouvre sur rien déçoit plus qu'elle ne sert.
  jeux: { primaire: false, college: false, lycee: false },

  // ÉTEINTE TANT QU'ON NE SAIT PAS. Une promotion affichée sur une panne
  // de lecture promet un cadeau que le serveur ne donnera pas — et le
  // parent, lui, aura payé en la voyant.
  offreLancement: {
    active: false, texte: '', fin: null, bandeau: false, heures: 0,
    formules: [], formulesTexte: '',
  },
};

/**
 * TOUT EST LU UNE FOIS PAR SESSION, SAUF QUE LE BANDEAU, LUI, CHANGE.
 *
 * Les autres drapeaux se décident une fois pour toutes : on n'ouvre pas
 * l'essai gratuit en milieu d'après-midi. Le bandeau, si — il existe pour
 * annoncer un incident PENDANT que les gens sont sur le site. Un message
 * que seuls verraient ceux qui rechargent la page arriverait toujours trop
 * tard.
 *
 * DEUX MINUTES : assez court pour qu’une annonce atteigne un parent en
 * cours de navigation, assez long pour que la charge reste nulle — une
 * requête de quelques octets, moins souvent que le battement de la
 * diffusion.
 */
const RAFRAICHISSEMENT_MS = 120_000;

let valeurs = PAR_DEFAUT;
let sonde = null;
const abonnes = new Set();

function interroger() {
  if (!sonde) {
    sonde = getReglagesPublics()
      .then(({ data }) => {
        valeurs = {
          modeTest: Boolean(data?.modeTest),
          // `?? true` et non `Boolean(...)` : une API antérieure à ce drapeau
          // ne l'envoie pas, et le lire comme « fermé » couperait l'essai
          // partout le temps d'un déploiement.
          essaisOuverts: data?.essaisOuverts ?? true,

          // ÉTEINT TANT QU'ON NE SAIT PAS, et ici ce n'est pas du confort :
          // une API muette ou une erreur réseau afficherait la page d'attente
          // à tout le monde. Le site se fermerait tout seul sur une panne de
          // lecture de réglage — exactement l'inverse de ce qu'on veut.
          maintenance: Boolean(data?.maintenance),

          // Le style du site, décidé par l'administration pour tout le monde.
          // Appliqué juste en dessous, avant de prévenir les abonnés.
          blueSky: Boolean(data?.blueSky),

          // La déclinaison de Blue Sky : une valeur inconnue ne pose rien.
          styleSaison: saisonValide(data?.styleSaison),

          // LE COUPE-CIRCUIT DU TEMPS RÉEL. `?? true` et non `Boolean(...)` :
          // une API antérieure à ce drapeau ne l'envoie pas, et le lire comme
          // éteint couperait le temps réel partout le temps d'un déploiement.
          fluxSse: data?.fluxSse ?? true,

          // Éteints par défaut : aucun jeu n'existe encore, et une lecture
          // ratée ne doit pas ouvrir une page vide aux enfants.
          jeux: {
            primaire: Boolean(data?.jeuxPrimaire),
            college: Boolean(data?.jeuxCollege),
            lycee: Boolean(data?.jeuxLycee),
          },

          // Le serveur n'envoie le texte que si le bandeau est allumé :
          // ici il n’y a rien à décider, juste à recopier. Une chaîne
          // vide vaut absence — un bandeau vide est un bandeau cassé.
          bandeau: data?.bandeau?.trim() ? data.bandeau : null,

          // `active` VIENT DU SERVEUR, on ne le recalcule pas ici. Il tient
          // déjà compte de l'échéance, et c'est lui qui décidera aussi de
          // créditer les heures au moment du paiement. Deux horloges qui
          // jugeraient séparément la même promotion finiraient par se
          // contredire — et c'est le visiteur qui verrait la contradiction.
          offreLancement: data?.offreLancement?.active
            ? {
                active: true,
                texte: data.offreLancement.texte || 'OFFRE LANCEMENT',
                fin: data.offreLancement.fin ?? null,

                // Le serveur a déjà croisé ce drapeau avec la vitalité de
                // l'offre : un décompte ne peut pas survivre à la
                // promotion qu'il annonce.
                bandeau: Boolean(data.offreLancement.bandeau),

                // CALCULÉ ICI, UNE FOIS. Le serveur envoie des minutes — la
                // seule unité qui ne perd rien. Les convertir dans chaque
                // écran ferait diverger les arrondis : « 1,5 h » ici et
                // « 2 h » là.
                heures: Math.round((data.offreLancement.minutesOffertes ?? 0) / 6) / 10,

                // LES CODES POUR RECONNAÎTRE LES CARTES, LE TEXTE POUR LE
                // DIRE. Le second est composé par le serveur, seul à
                // connaître les libellés : « Solo et Duo » plutôt que
                // « SOLO, DUO ».
                formules: Array.isArray(data.offreLancement.formules)
                  ? data.offreLancement.formules
                  : [],
                formulesTexte: data.offreLancement.formulesTexte ?? '',
              }
            : PAR_DEFAUT.offreLancement,
        };

        // ICI ET PAS DANS UN COMPOSANT : le style habille le site entier, y
        // compris la page d'attente qui remplace tous les composants.
        appliquerBlueSky(valeurs.blueSky, valeurs.styleSaison);

        // L'ÉCOUTE S'OUVRE APRÈS LA PREMIÈRE LECTURE, et non avant : c'est
        // cette lecture qui dit si le temps réel est allumé. Ici et pas dans un
        // composant — c'est le seul endroit que TOUS les écrans traversent,
        // page d'attente comprise. Rappelé à chaque relecture, il suit le
        // coupe-circuit dans les deux sens, sans rechargement.
        demarrerFluxReglages(oublierReglages, valeurs.fluxSse);

        abonnes.forEach((notifier) => notifier(valeurs));
        return valeurs;
      })
      .catch(() => {
        // Réglages illisibles : on laisse le produit ouvert. Le serveur, lui,
        // refusera ce qu'il doit refuser — c'est là qu'est la vraie garde,
        // pas dans l'affichage.
        //
        // ON INSTALLE QUAND MÊME LA RELECTURE PÉRIODIQUE, sans ouvrir de
        // connexion longue. Sans elle, un onglet ouvert pendant une panne
        // passagère resterait sur ses valeurs par défaut jusqu'au
        // rechargement, et ne verrait plus jamais un changement de style.
        demarrerFluxReglages(oublierReglages, false);

        // ON GARDE CE QU'ON SAVAIT, plutôt que de tout remettre à zéro.
        //
        // Camara, le 21/09/2026 : « pourquoi le bouton des jeux disparaît tout
        // le temps ? ». C'était ici. Une lecture ratée — un onglet qui revient
        // de veille, une seconde de réseau — renvoyait les valeurs par défaut,
        // et TOUS les abonnés en étaient notifiés : les jeux se refermaient,
        // l'offre de lancement disparaissait, le style repartait à l'état
        // d'origine. Le temps d'une requête, le produit changeait de visage.
        //
        // `valeurs` vaut PAR_DEFAUT tant que rien n'a jamais été lu : la
        // garantie d'ouverture décrite ci-dessus tient toujours au premier
        // chargement. Ce qui change, c'est qu'un échec PASSAGER ne fait plus
        // oublier ce qui était vrai il y a deux minutes.
        return valeurs;
      });
  }

  return sonde;
}

function useDrapeaux() {
  const [etat, setEtat] = useState(valeurs);

  useEffect(() => {
    let vivant = true;

    const notifier = (v) => { if (vivant) setEtat(v); };
    abonnes.add(notifier);

    interroger().then(notifier);

    return () => {
      vivant = false;
      abonnes.delete(notifier);
    };
  }, []);

  return etat;
}

/** Vrai quand le service est en accès privé. */
export function useModeTest() {
  return useDrapeaux().modeTest;
}

/**
 * Le message d'information à afficher en haut du site, ou `null`.
 *
 * SEUL DRAPEAU RELU EN COURS DE SESSION, et c'est sa raison d'être : il
 * sert à prévenir d'un incident pendant que les gens naviguent. Le
 * battement vit ici et non dans le composant — le cache est partagé, et
 * deux bandeaux affichés (le site, un aperçu) ne doivent pas doubler les
 * appels.
 */
export function useBandeau() {
  const message = useDrapeaux().bandeau;

  useEffect(() => {
    const battement = setInterval(oublierReglages, RAFRAICHISSEMENT_MS);

    return () => clearInterval(battement);
  }, []);

  return message;
}

/**
 * L'offre de lancement : est-elle vivante, comment s'appelle-t-elle, et
 * jusqu'à quand ?
 *
 * RENDUE EN BLOC plutôt qu'en trois crochets séparés. Les trois valeurs
 * n'ont de sens qu'ensemble : un texte sans drapeau habille une promotion
 * éteinte, une échéance sans drapeau fait tourner un compte à rebours pour
 * rien. Le composant qui les reçoit ne peut pas en oublier une.
 */
export function useOffreLancement() {
  return useDrapeaux().offreLancement;
}

/**
 * Vrai quand l'essai gratuit est proposé aux nouveaux venus.
 *
 * Fermé, l'accueil invite à s'inscrire au lieu de promettre du gratuit, et
 * aucun chemin n'ouvre plus d'essai. Le serveur refuse de toute façon : ce
 * drapeau ne fait qu'éviter de promettre ce qui sera refusé.
 */
export function useEssaisOuverts() {
  return useDrapeaux().essaisOuverts;
}

/**
 * Vrai quand le site affiche sa page d'attente.
 *
 * NE PROTÈGE RIEN, ET IL FAUT LE SAVOIR. C'est un rideau : l'API continue de
 * répondre normalement derrière — sans quoi l'administrateur qui vient de le
 * tirer ne pourrait plus le lever. Pour fermer vraiment le service, on arrête
 * l'API ; c'est un autre geste.
 */
export function useMaintenance() {
  return useDrapeaux().maintenance;
}

/**
 * Vrai quand le style « Blue Sky » habille le site.
 *
 * Presque personne n'en a besoin : le style se pose sur <html> et App.css fait
 * le reste. Seuls les écrans dont le BALISAGE change s'en servent — la page
 * d'attente, dont les deux versions ne sont pas faites des mêmes éléments.
 */
export function useBlueSky() {
  return useDrapeaux().blueSky;
}

/**
 * LES JEUX SONT-ILS OUVERTS POUR CET ENFANT ?
 *
 * Voulu par Camara le 20/09/2026 : trois interrupteurs d'administration, un
 * par cycle. Les jeux du primaire — très visuels, à manipuler — n'ont rien à
 * voir avec ceux du lycée et n'arriveront pas en même temps ; un drapeau
 * unique aurait forcé à tout ouvrir ou tout fermer.
 *
 * LE CYCLE VIENT DU SERVEUR, jamais du libellé de classe : « 3e » ne se
 * traduit pas en « College » sans une table, et cette table existe déjà
 * là-bas (NiveauScolaire.Cycle).
 *
 * Un cycle inconnu — profil incomplet, API plus ancienne — rend FAUX : mieux
 * vaut une porte manquante qu'une porte qui n'ouvre sur rien.
 */
export function useJeuxOuverts(cycle) {
  // Défensif : un jeu de valeurs venu d'une version antérieure du module
  // n'aurait pas cette clé, et une porte fermée vaut mieux qu'un écran blanc.
  const jeux = useDrapeaux().jeux ?? {};

  switch ((cycle ?? '').toLowerCase()) {
    case 'primaire': return Boolean(jeux.primaire);
    case 'college': return Boolean(jeux.college);
    case 'lycee': return Boolean(jeux.lycee);
    default: return false;
  }
}

/**
 * Oublie les valeurs retenues. Appelé après un changement dans
 * l'administration : sans ça, l'administrateur qui bascule un interrupteur ne
 * verrait rien bouger avant d'avoir rechargé la page.
 */
export function oublierReglages() {
  sonde = null;
  interroger();
}

/** Ancien nom, gardé le temps que les appels existants migrent. */
export const oublierModeTest = oublierReglages;
